// Unpublished Work © 2026 Deere & Company.
import React from 'react';
import {debounce} from 'lodash';
import {JOB} from 'OnLabor/workboard/constants/workboard-creation-flows';
import {MINUTES_PER_HOUR} from 'Ui/constants/time-constants';

const KEY_SEPARATOR = '::';
const DEBOUNCE = 200;

export function getGroupKey(groupBy, item) {
    const key = groupBy === JOB ? item.jobId : item.operatorId;

    return key;
}

export function buildWorkItemKey(jobId, operatorId) {
    return `${jobId}${KEY_SEPARATOR}${operatorId}`;
}

export function updateWorkItem(key, values, defaultValues, workItems) {
    const newValues = {
        ...values
    };
    const current =
        workItems[key] ??
        {
            ...defaultValues.current ?? {}
        };

    if (Object.hasOwn(newValues, 'actualDuration')) {
        newValues.actualDuration = newValues.actualDuration * MINUTES_PER_HOUR;
    }
    const updatedWorkItems = {
        ...workItems,
        [key]: {
            ...current,
            ...newValues
        }
    };

    return updatedWorkItems;
}

// Returns the operatorId and jobId from a row based on the flow type
export function mapIdsForWorkItem(groupBy, rowId, id) {
    const ids = groupBy === 'job' ?
        {
            jobId: rowId,
            operatorId: id
        } :
        {
            jobId: id,
            operatorId: rowId
        };

    return ids;
}

// Compacts the sequence of the workItems 1...n
export function normalizeSeqByGroup(items, groupBy) {
    const next = {
        ...items
    };
    const byGroup = new Map();

    for (const [k, it] of Object.entries(items)) {
        const g = getGroupKey(groupBy, it);
        const arr = byGroup.get(g) || [];

        arr.push([k, it]);
        byGroup.set(g, arr);
    }

    for (const group of byGroup.values()) {
        group.sort(([ka, a], [kb, b]) => {
            const sa = Number.isFinite(a.seq) ? a.seq : Number.POSITIVE_INFINITY;
            const sb = Number.isFinite(b.seq) ? b.seq : Number.POSITIVE_INFINITY;

            if (sa !== sb) {
                return sa - sb;
            }
            return String(ka).localeCompare(String(kb));
        });

        group.forEach(([k, it], index) => {
            next[k] = {
                ...it,
                seq: index + 1
            };
        });
    }

    return next;
}

// Adds new workItems to the end of the list and sets the sequence value
export function appendNewItemsToEndByGroup(items, groupBy) {
    const next = {
        ...items
    };
    const byGroup = new Map();

    for (const [k, it] of Object.entries(items)) {
        const g = getGroupKey(groupBy, it);
        const arr = byGroup.get(g) || [];

        arr.push([k, it]);
        byGroup.set(g, arr);
    }

    for (const group of byGroup.values()) {
        const withSeq = group.filter(([, it]) => Number.isFinite(it.seq));
        const withoutSeq = group.filter(([, it]) => !Number.isFinite(it.seq));

        withSeq.sort(([, a], [, b]) => a.seq - b.seq);

        let base = withSeq.length;

        for (const [k, it] of withoutSeq) {
            base += 1;
            next[k] = {
                ...it,
                seq: base
            };
        }
    }

    return normalizeSeqByGroup(next, groupBy);
}

export function getCurrentWorkItemKeys(multiSelectValue, groupBy, rowIds = null) {
    if (!multiSelectValue) {
        return null;
    }

    const keys = new Set();

    for (const rowId of rowIds) {
        const selected = multiSelectValue[rowId] || [];

        for (const id of selected) {
            keys.add(groupBy === JOB ? buildWorkItemKey(rowId, id) : buildWorkItemKey(id, rowId));
        }
    }
    return keys;
}

export function filterWorkItems(workItems, ids = null, groupBy, rowIds = null, multiSelectValue) {
    const existingKeys = getCurrentWorkItemKeys(multiSelectValue, groupBy, rowIds);
    const entries = Object.entries(workItems).filter(([k, item]) => {
        const groupKey = getGroupKey(groupBy, item);

        if (existingKeys && existingKeys.size > 0) {
            return existingKeys.has(k);
        }

        if (rowIds && !rowIds.includes(groupKey)) {
            return false;
        }

        const operatorAllowed = groupBy !== JOB || ids.includes(item.operatorId);
        const jobAllowed = groupBy === JOB || ids.includes(item.jobTemplateId ?? item.jobId);

        return operatorAllowed && jobAllowed;
    });

    const byGroup = new Map();

    for (const [k, it] of entries) {
        const g = getGroupKey(groupBy, it);
        const arr = byGroup.get(g) || [];

        arr.push([k, it]);
        byGroup.set(g, arr);
    }

    const result = {};

    for (const group of byGroup.values()) {
        group.sort(([ka, a], [kb, b]) => {
            const sa = Number.isFinite(a.seq) ? a.seq : Number.POSITIVE_INFINITY;
            const sb = Number.isFinite(b.seq) ? b.seq : Number.POSITIVE_INFINITY;

            if (sa !== sb) {
                return sa - sb;
            }
            return String(ka).localeCompare(String(kb));
        });
        group.forEach(([k, it], index) => {
            result[k] = {
                ...it,
                seq: index + 1
            };
        });
    }

    return result;
}

export function renderExistingWorkItems(
    workItems,
    setMultiSelectValue,
    selectedIds,
    rowIds,
    saveWorkItems,
    groupBy
) {
    const filtered = filterWorkItems(workItems, selectedIds, groupBy, rowIds);

    const normalized = normalizeSeqByGroup(filtered, groupBy);

    const existingValues = {};

    for (const item of Object.values(normalized)) {
        const groupKey = getGroupKey(groupBy, item);
        const relatedId = groupKey === item.jobId ? item.operatorId : item.jobId;
        const notRemoved = rowIds.includes(groupKey);

        if (!existingValues[groupKey] && notRemoved) {
            existingValues[groupKey] = [];
        }

        if (!existingValues[groupKey]?.includes(relatedId) && notRemoved) {
            existingValues[groupKey].push(relatedId);
        }
    }

    setMultiSelectValue(existingValues);

    saveWorkItems(normalized);
}

export function sortWorkItemsBySeq(workItems) {
    workItems.sort((a, b) => {
        const sa = Number.isFinite(a.seq) ? a.seq : Number.POSITIVE_INFINITY;
        const sb = Number.isFinite(b.seq) ? b.seq : Number.POSITIVE_INFINITY;

        if (sa !== sb) {
            return sa - sb;
        }
        return a.compositeKey.localeCompare(b.compositeKey);
    });
    return workItems;
}

export function getAssignedEquipment(workItems) {
    const assignedEquipments = Object.values(workItems)
        .flatMap((w) => w.equipment || []);

    const assignedIds = new Set(assignedEquipments.map((eq) => eq.equipmentId));

    return [...assignedIds];
}

// Debounce the update of workItems to avoid lagging in desktop, for mobile don't update until user explicitly confirms
export function createPersist(isMobile, saveWorkItems) {
    if (isMobile) {
        return {
            run: () => { },
            flush: () => { },
            cancel: () => { }
        };
    }

    const debounced = debounce((newWorkItems) => {
        React.startTransition(() => saveWorkItems(newWorkItems));
    }, DEBOUNCE);

    return {
        run: debounced,
        flush: debounced.flush,
        cancel: debounced.cancel
    };
}
